// PrivateRoute Component - Basic Authentication Guard
import React, { Suspense } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../shared/hooks';
import { LoadingSpinner } from '../../shared/components';

/**
 * PrivateRoute component that requires authentication
 * Redirects to login if user is not authenticated
 *
 * @param {Object} props - Component props
 * @param {React.Component} props.children - Child components to render
 * @param {string} props.redirectTo - Redirect path for unauthenticated users
 * @returns {React.Component} Protected route or redirect
 */
export const PrivateRoute = ({ children, redirectTo = '/login' }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="route-loading-container">
        <LoadingSpinner size="large" message="Checking authentication..." />
      </div>
    );
  }

  // Redirect to login if not authenticated, preserving the intended destination
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Render protected content with Suspense boundary for lazy loading
  return (
    <Suspense
      fallback={
        <div className="route-loading-container">
          <LoadingSpinner size="medium" message="Loading..." />
        </div>
      }
    >
      {children}
    </Suspense>
  );
};

export default PrivateRoute;
